import { LEFT_NAV_CLASS } from "@app/config/const";
import { cn } from "@core-ui/utils/cn";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from "@ui-kit/Sidebar";
import { type CSSProperties, forwardRef, type PointerEventHandler, type TransitionEventHandler } from "react";
import { VIEWPORT_PADDING } from "../../../../ui-kit/lib/floating";
import { COLLAPSED_PANEL_OFFSET, NAV_Z_INDEX } from "./leftNavigationLayoutConstants";

// Breakpoints use the mobile snapshot during SSR; the bootstrap attribute already has the saved desktop state.
const INITIAL_DESKTOP_POSITION =
	"lg:[html[data-left-sidebar-pinned=true]_&]:!left-0 lg:[html[data-left-sidebar-pinned=false]_&]:!left-[calc(var(--sidebar-width)*-1)]";

const EDGE_TO_EDGE_PANEL = [
	"h-full",
	"[&>[data-sidebar=sidebar]]:!pt-[var(--catalog-titlebar-offset,0rem)]",
	"[&>[data-sidebar=sidebar]]:!overflow-hidden",
	"[&>[data-sidebar=sidebar]]:!rounded-none",
	"[&>[data-sidebar=sidebar]]:!border-y-0",
	"[&>[data-sidebar=sidebar]]:!border-l-0",
	"[&>[data-sidebar=sidebar]]:!bg-primary-bg",
	"[&>[data-sidebar=sidebar]]:!shadow-none",
].join(" ");

const FLOATING_PANEL = [
	"!top-[var(--catalog-titlebar-offset,0rem)]",
	"[&>[data-sidebar=sidebar]]:!overflow-hidden",
	"[&>[data-sidebar=sidebar]]:!rounded-xl",
	"[&>[data-sidebar=sidebar]]:!bg-primary-bg",
	"[&>[data-sidebar=sidebar]]:!shadow-glass-lg",
].join(" ");

// The app-level macOS drag area sits below the sidebar, and the modal mobile sheet disables pointer events outside itself.
const TITLEBAR_DRAG_REGION = "fixed inset-x-0 top-0 h-[var(--catalog-titlebar-offset,0rem)] select-none";

const floatingPanelStyle = (hasBottom: boolean): CSSProperties => ({
	bottom: hasBottom ? COLLAPSED_PANEL_OFFSET : 0,
	height: "auto",
	marginTop: COLLAPSED_PANEL_OFFSET,
	padding: VIEWPORT_PADDING,
});

const panelPosition = (isMobile: boolean, isVisible: boolean) => {
	if (isMobile) return INITIAL_DESKTOP_POSITION;
	return isVisible ? "!left-0" : "!left-[calc(var(--sidebar-width)*-1)]";
};

interface LeftNavigationPanelProps {
	content: JSX.Element;
	top?: JSX.Element;
	bottom?: JSX.Element;
	isCollapsed: boolean;
	isMobile: boolean;
	isVisible: boolean;
	isResizing: boolean;
	onPointerOver: PointerEventHandler<HTMLDivElement>;
	onPointerLeave: PointerEventHandler<HTMLDivElement>;
	onTransitionEnd?: TransitionEventHandler<HTMLDivElement>;
}

export const LeftNavigationPanel = forwardRef<HTMLDivElement, LeftNavigationPanelProps>(
	(
		{
			content,
			top,
			bottom,
			isCollapsed,
			isMobile,
			isVisible,
			isResizing,
			onPointerOver,
			onPointerLeave,
			onTransitionEnd,
		},
		ref,
	) => (
		<Sidebar
			className={cn(
				"group-data-[side=left]:border-none print:hidden contain-layout contain-style left-navigation-content p-0",
				LEFT_NAV_CLASS,
				NAV_Z_INDEX,
				"!transition-[left] !duration-300 !ease-out motion-reduce:!transition-none",
				isCollapsed ? FLOATING_PANEL : EDGE_TO_EDGE_PANEL,
				panelPosition(isMobile, isVisible),
				isResizing && "!transition-none",
			)}
			collapsible="offcanvas"
			onPointerLeave={onPointerLeave}
			onPointerOver={onPointerOver}
			onTransitionEnd={onTransitionEnd}
			ref={ref}
			side="left"
			style={isCollapsed ? floatingPanelStyle(!!bottom) : undefined}
			variant="floating"
		>
			{!isCollapsed && <div aria-hidden className={TITLEBAR_DRAG_REGION} data-tauri-drag-region />}
			{top && (
				// The mobile sidebar is a Sheet that drops the panel className, so it clears the
				// native titlebar through its own header instead.
				<SidebarHeader className={cn("gap-0 !p-0", isMobile && "!pt-[var(--catalog-titlebar-offset,0rem)]")}>
					{top}
				</SidebarHeader>
			)}
			<SidebarContent className="min-h-0 [&>div:first-of-type]:flex-1">
				{content}
				{!isMobile && !isCollapsed && (
					<div
						aria-hidden
						className={cn("shrink-0", bottom ? "h-[4.375rem]" : "h-2")}
						data-testid="left-navigation-bottom-scroll-space"
					/>
				)}
			</SidebarContent>
			{isMobile && bottom && <SidebarFooter className="gap-0 p-0">{bottom}</SidebarFooter>}
		</Sidebar>
	),
);
